import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Button, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import indigentModuleIcon from '../assets/images/icons/indigentModule.svg';
import recordModuleIcon from '../assets/images/icons/recordModule.svg';
import riskModuleIcon from '../assets/images/icons/riskModule.svg';
import verificationModuleIcon from '../assets/images/icons/verificationModule.svg';
import PasswordExpiryDialog from '../components/PasswordExpiryDialog';
import ChangePasswordModal from '../components/ChangePasswordModal';
import { passwordExpiryUtils } from '../utils/passwordExpiryUtils';
import { isVerificationSystemEnabled } from '@/utils/featureUtils';

const ModuleCard: React.FC<{
    title: string;
    description: string;
    icon: string;
    showLaunch?: boolean;
    onClick?: () => void;
}> = ({ title, description, icon, showLaunch, onClick }) => {
    return (
        <Card
            sx={{
                height: '100%',
                borderRadius: { xs: '12px', sm: '16px' },
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#fff',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                    transform: 'translateY(-4px)',
                },
                overflow: 'hidden', // Prevent content overflow
            }}
        >
            <CardContent sx={{
                p: { xs: 2, sm: 3 },
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden' // Prevent content overflow
            }}>
                <Box
                    component="img"
                    src={icon}
                    alt={title}
                    sx={{
                        width: 48,
                        height: 48,
                        mb: 2
                    }}
                />
                <Typography
                    variant="h6"
                    sx={{
                        fontSize: { xs: '16px', sm: '20px' },
                        fontWeight: 600,
                        color: '#132F5F',
                        mb: 1,
                        lineHeight: 1.2
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: '#6b7280',
                        mb: 2,
                        fontSize: { xs: '12px', sm: '14px' },
                        lineHeight: '1.5',
                        flexGrow: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical'
                    }}
                >
                    {description}
                </Typography>
                <Box sx={{ minHeight: 48, display: 'flex', alignItems: 'flex-end' }}>
                    {showLaunch ? (
                        <Button
                            onClick={onClick}
                            sx={{
                                backgroundColor: '#0A1D56',
                                color: '#fff',
                                px: { xs: 3, sm: 4 },
                                py: { xs: 1, sm: 1.5 },
                                borderRadius: { xs: '8px', sm: '12px' },
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: { xs: '12px', sm: '14px' },
                                width: { xs: '100%', sm: 'auto' },
                                '&:hover': {
                                    backgroundColor: '#091B4C',
                                },
                            }}
                        >
                            Launch →
                        </Button>
                    ) : (
                        <Box sx={{ height: 48 }} /> // Placeholder to maintain consistent height
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

const HomePage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    // Password expiry dialog state
    const [showPasswordExpiryDialog, setShowPasswordExpiryDialog] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [isChangePasswordFromExpiry, setIsChangePasswordFromExpiry] = useState(false);
    const [passwordExpiryInfo, setPasswordExpiryInfo] = useState({
        daysUntilExpiry: 0,
        isPasswordExpired: false,
    });

    // Check password expiry on component mount
    // This logic should be ideally moved into the protected route component
    useEffect(() => {
        const expiryCheck = passwordExpiryUtils.checkPasswordExpiry();
        if (expiryCheck.shouldShowDialog) {
            setPasswordExpiryInfo({
                daysUntilExpiry: expiryCheck.daysUntilExpiry,
                isPasswordExpired: expiryCheck.isPasswordExpired,
            });
            setShowPasswordExpiryDialog(true);
            // Mark dialog as shown to prevent showing again
            passwordExpiryUtils.markDialogAsShown();
        }
    }, []);

    const handleIndigentLaunch = () => {
        navigate('/indigent-dashboard');
    };


    const handleSkipPasswordExpiry = () => {
        setShowPasswordExpiryDialog(false);
    };

    const handleChangePassword = () => {
        setShowPasswordExpiryDialog(false);
        setIsChangePasswordFromExpiry(true);
        setShowChangePasswordModal(true);
    };

    const handleChangePasswordModalClose = () => {
        setShowChangePasswordModal(false);
        setIsChangePasswordFromExpiry(false);
    };

    const handleVerificationSystemLaunch = () => {
        navigate('/verification/dashboard');
    };

    return (
        <Box sx={{
            p: isMobile ? 2 : 4,
            backgroundColor: '#FEFBFF',
            minHeight: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center', // Center vertically
            pt: { xs: 10, sm: 0 }, // Account for fixed TopBar height (64px + padding)
            pb: { xs: 4, sm: 0 } // Add bottom padding for better spacing
        }}>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: { xs: 3, sm: 4 },
                maxWidth: { xs: '100%', sm: '800px', md: '1200px' },
                margin: '0 auto',
                width: '100%',
                px: { xs: 1, sm: 0 } // Add horizontal padding on mobile
            }}>
                <ModuleCard
                    title="Indigent Management System"
                    description="Manage and process applications for indigent support efficiently. Track eligibility, approvals, and support status all in one place."
                    icon={indigentModuleIcon}
                    showLaunch
                    onClick={handleIndigentLaunch}
                />
                <ModuleCard
                    title="Records Management System"
                    description="Securely store, retrieve, and manage resident or applicant records. Ensure compliance and access critical documents quickly when needed."
                    icon={recordModuleIcon}
                />
                <ModuleCard
                    title="Risk Management System"
                    description="Identify, assess, and mitigate risks effectively. Monitor trends, flag issues early, and generate reports for better decision-making."
                    icon={riskModuleIcon}
                />
                <ModuleCard
                    title="Verification System"
                    description="Verify submitted information and documentation with ease. This module helps ensure accuracy, eligibility, and reduces fraud."
                    icon={verificationModuleIcon}
                    showLaunch={isVerificationSystemEnabled()}
                    onClick={handleVerificationSystemLaunch}
                />
            </Box>

            {/* Password Expiry Dialog */}
            <PasswordExpiryDialog
                open={showPasswordExpiryDialog}
                onClose={handleSkipPasswordExpiry}
                onSkip={handleSkipPasswordExpiry}
                onChangePassword={handleChangePassword}
                daysUntilExpiry={passwordExpiryInfo.daysUntilExpiry}
                isPasswordExpired={passwordExpiryInfo.isPasswordExpired}
            />

            {/* Change Password Modal */}
            <ChangePasswordModal
                open={showChangePasswordModal}
                onClose={handleChangePasswordModalClose}
                persistent={isChangePasswordFromExpiry && passwordExpiryInfo.isPasswordExpired}
            />
        </Box>
    );
};

export default HomePage;